import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import AdminPage from '../../components/admin/AdminPage';
import notificationsService, {
  AudienceEstimate,
  NotificationAudienceKind,
  NotificationCategory,
  PreviewResult,
} from '../../services/NotificationsService';

const AUDIENCE_KINDS: NotificationAudienceKind[] = [
  'ALL_USERS',
  'DONORS',
  'CIRCLE_MEMBERS',
  'CHALLENGE_PARTICIPANTS',
  'INACTIVE_USERS',
  'SPECIFIC_USERS',
];

const CATEGORIES: NotificationCategory[] = [
  'ADMIN_BROADCAST',
  'ACCOUNT_UPDATE',
  'PAYMENT_ALERT',
];

const NotificationComposerPage: React.FC = () => {
  const navigate = useNavigate();

  const [titleEn, setTitleEn] = useState('');
  const [titleAr, setTitleAr] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [bodyAr, setBodyAr] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [category, setCategory] = useState<NotificationCategory>('ADMIN_BROADCAST');

  const [audienceKind, setAudienceKind] = useState<NotificationAudienceKind>('ALL_USERS');
  const [circleId, setCircleId] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [inactiveDays, setInactiveDays] = useState('30');
  const [specificUserIds, setSpecificUserIds] = useState('');

  const [scheduledFor, setScheduledFor] = useState('');
  const [timezone, setTimezone] = useState('Africa/Cairo');

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [audienceEstimate, setAudienceEstimate] = useState<AudienceEstimate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const audienceFilter = useMemo(() => {
    switch (audienceKind) {
      case 'CIRCLE_MEMBERS':
        return circleId ? { circleId } : {};
      case 'CHALLENGE_PARTICIPANTS':
        return challengeId ? { challengeId } : {};
      case 'INACTIVE_USERS':
        return { inactiveDays: Number(inactiveDays) || 30 };
      case 'SPECIFIC_USERS':
        return {
          userIds: specificUserIds
            .split(/[,\s]+/)
            .map((id) => id.trim())
            .filter(Boolean),
        };
      default:
        return {};
    }
  }, [audienceKind, circleId, challengeId, inactiveDays, specificUserIds]);

  const canPreview = titleEn && titleAr && bodyEn && bodyAr;

  const handlePreview = useCallback(async () => {
    if (!canPreview) return;
    setError(null);
    try {
      const result = await notificationsService.preview({
        titleEn,
        titleAr,
        bodyEn,
        bodyAr,
        deepLink: deepLink || undefined,
      });
      setPreview(result);
    } catch {
      setError('Preview failed.');
    }
  }, [canPreview, titleEn, titleAr, bodyEn, bodyAr, deepLink]);

  // Auto-preview on content change (debounced by React batching).
  useEffect(() => {
    if (canPreview) void handlePreview();
    else setPreview(null);
  }, [canPreview, handlePreview]);

  const handleEstimate = useCallback(async () => {
    setError(null);
    try {
      const result = await notificationsService.estimateAudience(
        audienceKind,
        audienceFilter,
      );
      setAudienceEstimate(result);
    } catch {
      setError('Failed to estimate audience.');
    }
  }, [audienceKind, audienceFilter]);

  useEffect(() => {
    void handleEstimate();
  }, [handleEstimate]);

  const submit = useCallback(
    async (mode: 'draft' | 'schedule' | 'now') => {
      if (!canPreview) {
        setError('Fill in both languages before submitting.');
        return;
      }
      if (mode === 'schedule' && !scheduledFor) {
        setError('Pick a scheduled time.');
        return;
      }
      setSubmitting(true);
      setError(null);
      setNotice(null);
      try {
        const result = await notificationsService.createScheduled({
          category,
          titleEn,
          titleAr,
          bodyEn,
          bodyAr,
          deepLink: deepLink || undefined,
          audienceKind,
          audienceFilter,
          scheduledFor: mode === 'schedule' ? new Date(scheduledFor).toISOString() : undefined,
          timezone,
          saveAsDraft: mode === 'draft',
          sendNow: mode === 'now',
        });
        setNotice(`Saved as ${result.status}.`);
        navigate(`/notifications/${result.id}`);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ?? 'Failed to save the notification.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      canPreview,
      scheduledFor,
      category,
      titleEn,
      titleAr,
      bodyEn,
      bodyAr,
      deepLink,
      audienceKind,
      audienceFilter,
      timezone,
      navigate,
    ],
  );

  return (
    <AdminPage
      title="New notification"
      description="Compose bilingual copy, target an audience, and either send immediately or schedule."
    >
      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content
              </Typography>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={category}
                    onChange={(e: SelectChangeEvent) =>
                      setCategory(e.target.value as NotificationCategory)
                    }
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Title (EN)"
                  value={titleEn}
                  onChange={(e) => setTitleEn(e.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Title (AR)"
                  value={titleAr}
                  onChange={(e) => setTitleAr(e.target.value)}
                  inputProps={{ maxLength: 120, dir: 'rtl' }}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Body (EN)"
                  value={bodyEn}
                  onChange={(e) => setBodyEn(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Body (AR)"
                  value={bodyAr}
                  onChange={(e) => setBodyAr(e.target.value)}
                  inputProps={{ maxLength: 500, dir: 'rtl' }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Deep link (optional)"
                  placeholder="https://join.myefb.app/app/circles/<id>"
                  value={deepLink}
                  onChange={(e) => setDeepLink(e.target.value)}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Audience
              </Typography>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Audience</InputLabel>
                  <Select
                    label="Audience"
                    value={audienceKind}
                    onChange={(e: SelectChangeEvent) =>
                      setAudienceKind(e.target.value as NotificationAudienceKind)
                    }
                  >
                    {AUDIENCE_KINDS.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {audienceKind === 'CIRCLE_MEMBERS' && (
                  <TextField
                    size="small"
                    label="Circle id"
                    value={circleId}
                    onChange={(e) => setCircleId(e.target.value)}
                  />
                )}
                {audienceKind === 'CHALLENGE_PARTICIPANTS' && (
                  <TextField
                    size="small"
                    label="Challenge id"
                    value={challengeId}
                    onChange={(e) => setChallengeId(e.target.value)}
                  />
                )}
                {audienceKind === 'INACTIVE_USERS' && (
                  <TextField
                    size="small"
                    type="number"
                    label="Inactive for (days)"
                    value={inactiveDays}
                    onChange={(e) => setInactiveDays(e.target.value)}
                  />
                )}
                {audienceKind === 'SPECIFIC_USERS' && (
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    label="User ids (comma or newline separated)"
                    value={specificUserIds}
                    onChange={(e) => setSpecificUserIds(e.target.value)}
                  />
                )}
                <Box>
                  <Chip
                    label={
                      audienceEstimate == null
                        ? 'Estimating…'
                        : audienceEstimate.count == null
                        ? `Not resolvable (${audienceEstimate.reason ?? 'unknown'})`
                        : `≈ ${audienceEstimate.count.toLocaleString()} users`
                    }
                    color={audienceEstimate?.count == null ? 'warning' : 'info'}
                    variant="outlined"
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schedule
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Scheduled for"
                  InputLabelProps={{ shrink: true }}
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                />
                <TextField
                  size="small"
                  label="Timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                />
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => submit('draft')}
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() => submit('schedule')}
            >
              Schedule
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={submitting}
              onClick={() => submit('now')}
            >
              Send now
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          <PreviewCard label="English" preview={preview?.en} />
          <PreviewCard label="Arabic" preview={preview?.ar} rtl />
        </Grid>
      </Grid>
    </AdminPage>
  );
};

const PreviewCard: React.FC<{
  label: string;
  rtl?: boolean;
  preview?: { title: string; body: string; deepLink: string | null } | null;
}> = ({ label, preview, rtl }) => (
  <Paper
    variant="outlined"
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      direction: rtl ? 'rtl' : 'ltr',
      bgcolor: 'background.default',
    }}
  >
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ mt: 1 }}>
      {preview?.title || <em>Fill in {label.toLowerCase()} content…</em>}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {preview?.body}
    </Typography>
    {preview?.deepLink ? (
      <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
        {preview.deepLink}
      </Typography>
    ) : null}
  </Paper>
);

export default NotificationComposerPage;
